import { Formik } from "formik";

import {
  FormikInput,
  FormikStateSelect,
  FormikZipInput,
} from "@src/shared/components/forms";
import { HeaderText } from "@src/shared/components/general/HeaderText";
import { Text } from "@src/shared/components/general/Text";
import {
  HorizontalFlexbox,
  VerticalFlexbox,
} from "@src/shared/components/layout/Flexbox";
import { Button } from "@src/shared/components/ui/button";
import { Spinner } from "@src/shared/components/ui/spinner";

import {
  PracticeInfoFormValidationSchema,
  type PracticeInfoFormValues,
} from "../../../../domain";

export interface PracticeInfoSettingsPageV2Props {
  initialValues: PracticeInfoFormValues;
  isReadOnly?: boolean;
  isSaving?: boolean;
  saveError?: string | null;
  onSubmit: (values: PracticeInfoFormValues) => void | Promise<void>;
}

export function PracticeInfoSettingsPageV2({
  initialValues,
  isReadOnly = false,
  isSaving = false,
  saveError = null,
  onSubmit,
}: PracticeInfoSettingsPageV2Props) {
  return (
    <VerticalFlexbox
      className="mx-auto w-full max-w-[720px] px-4 py-6 md-stitch:px-8 md-stitch:py-8"
      gap="none"
    >
      <HeaderText tag="h2" className="mb-1 text-[22px] tracking-tight">
        Practice info
      </HeaderText>
      <Text appearance="subdued" className="mb-8" sizing="sm">
        Used on prescriptions, claims, and patient-facing surfaces.
      </Text>

      <Formik<PracticeInfoFormValues>
        enableReinitialize
        initialValues={initialValues}
        validationSchema={PracticeInfoFormValidationSchema}
        onSubmit={async (values) => {
          await onSubmit(values);
        }}
      >
        {({ dirty, handleSubmit, isSubmitting }) => {
          const submitting = isSaving || isSubmitting;
          const fieldsDisabled = isReadOnly || submitting;

          return (
            <VerticalFlexbox
              as="form"
              className="w-full max-w-[520px]"
              gap="none"
              onSubmit={handleSubmit}
            >
              <VerticalFlexbox
                aria-labelledby="practice-info-heading"
                as="section"
                gap="md"
              >
                <Text as="h3" className="sr-only" id="practice-info-heading">
                  Practice profile
                </Text>

                <FormikInput
                  autoComplete="organization"
                  disabled={fieldsDisabled}
                  label="Practice name"
                  name="name"
                  required
                />
                <FormikInput
                  autoComplete="address-line1"
                  disabled={fieldsDisabled}
                  label="Address"
                  name="addressLine1"
                  required
                />
                <FormikInput
                  autoComplete="address-line2"
                  disabled={fieldsDisabled}
                  label="Address line 2"
                  name="addressLine2"
                />
                <HorizontalFlexbox
                  className="flex-col sm-stitch:grid sm-stitch:grid-cols-[2fr_1fr_1fr]"
                  gap="md"
                >
                  <FormikInput
                    autoComplete="address-level2"
                    containerClassName="min-w-0"
                    disabled={fieldsDisabled}
                    label="City"
                    name="city"
                    required
                  />
                  <FormikStateSelect
                    containerClassName="min-w-0"
                    disabled={fieldsDisabled}
                    label="State"
                    name="state"
                    required
                  />
                  <FormikZipInput
                    autoComplete="postal-code"
                    containerClassName="min-w-0"
                    disabled={fieldsDisabled}
                    label="ZIP"
                    name="zip"
                    required
                  />
                </HorizontalFlexbox>

                {saveError ? (
                  <Text appearance="danger" role="alert" sizing="sm">
                    {saveError}
                  </Text>
                ) : null}

                {isReadOnly ? null : (
                  <Button
                    className="w-full sm-stitch:w-auto"
                    disabled={!dirty || submitting}
                    type="submit"
                  >
                    {submitting ? <Spinner className="mr-1" size="sm" /> : null}
                    {submitting ? "Saving..." : "Save changes"}
                  </Button>
                )}
              </VerticalFlexbox>
            </VerticalFlexbox>
          );
        }}
      </Formik>
    </VerticalFlexbox>
  );
}
